import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { SITE, IMAGES } from "@/data/site";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Our Work", href: "#our-work" },
  { label: "Financing", href: "#financing" },
  { label: "Reviews", href: "#reviews" },
  { label: "Warranties", href: "#warranties" },
  { label: "FAQ", href: "#faq" },
];

export const Header = ({ solid = false }) => {
  const { openQuote } = useQuote();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const showSolid = solid || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        showSolid ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <a href="#top" data-testid="logo-link" className="flex items-center gap-2.5 group">
            <img
              src={IMAGES.logoBadge}
              alt="Templar Armor Roofing & Construction logo"
              className="h-12 w-12 rounded-lg object-contain bg-white p-0.5 shadow-sm group-hover:scale-105 transition-transform"
            />
            <div className="leading-none">
              <span className={`block font-display font-extrabold text-lg ${showSolid ? "text-slate-900" : "text-white"}`}>
                TEMPLAR ARMOR
              </span>
              <span className={`block text-[10px] tracking-[0.22em] font-semibold ${showSolid ? "text-red-700" : "text-red-500"}`}>
                ROOFING & CONSTRUCTION
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.href.slice(1)}`}
                className={`text-sm font-semibold transition-colors hover:text-red-700 ${
                  showSolid ? "text-slate-700" : "text-white/90"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="header-call-link"
              className={`flex items-center gap-2 text-sm font-bold transition-colors ${
                showSolid ? "text-slate-900 hover:text-red-700" : "text-white hover:text-red-400"
              }`}
            >
              <Phone className="h-4 w-4" /> {SITE.phone}
            </a>
            <Button
              data-testid="header-quote-btn"
              onClick={() => openQuote()}
              className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full px-6 hover:scale-105 transition-transform"
            >
              Get a Free Quote
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${showSolid ? "text-slate-900" : "text-white"}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-3">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setMenuOpen(false)}
              className="block text-slate-800 font-semibold py-1.5"
            >
              {l.label}
            </a>
          ))}
          <div className="flex flex-col gap-3 pt-2">
            <a href={SITE.phoneHref} className="flex items-center gap-2 font-bold text-slate-900">
              <Phone className="h-4 w-4 text-red-700" /> {SITE.phone}
            </a>
            <Button
              data-testid="mobile-quote-btn"
              onClick={() => { setMenuOpen(false); openQuote(); }}
              className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full"
            >
              Get a Free Quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
